
import React, { PureComponent } from "react";
import { b64toBlob } from "../../DataServices/Utility";
import { Box, Typography } from "@mui/material";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import ReactPlayer from "react-player";

export class VideoPlayer extends PureComponent {
    constructor(props) {
        super(props);

        const isMobile =
            typeof navigator !== "undefined" &&
            /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

        this.state = {
            streamBase: "https://mylivesphere.com/api/videostream",
            isMobile,
            muted: isMobile,   // iOS autoplay requires muted
            playing: isMobile, // autoplay on mobile

            // ✅ controls behavior
            showControls: false,
        };

        // timers/flags
        this._controlsHideTimer = null;
        this._longPressTimer = null;
        this._suppressNextClick = false;

        // tune these if you want
        this.LONG_PRESS_MS = 450;
        this.CONTROLS_HIDE_MS = 3500;
    }

    clearControlsHideTimer = () => {
        if (this._controlsHideTimer) {
            clearTimeout(this._controlsHideTimer);
            this._controlsHideTimer = null;
        }
    };

    scheduleControlsHide = (ms = this.CONTROLS_HIDE_MS) => {
        this.clearControlsHideTimer();
        this._controlsHideTimer = setTimeout(() => {
            this.setState({ showControls: false });
        }, ms);
    };

    showControlsTemporarily = () => {
        // show + keep alive
        if (!this.state.showControls) {
            this.setState({ showControls: true });
        }
        this.scheduleControlsHide(this.CONTROLS_HIDE_MS);
    };

    // Long press handling (touch + mouse)
    handlePointerDown = () => {
        // start long press detector
        if (this._longPressTimer) clearTimeout(this._longPressTimer);

        this._longPressTimer = setTimeout(() => {
            this._suppressNextClick = true; // stop the click that follows a long-press
            this.showControlsTemporarily();
        }, this.LONG_PRESS_MS);
    };

    handlePointerUpOrCancel = () => {
        if (this._longPressTimer) {
            clearTimeout(this._longPressTimer);
            this._longPressTimer = null;
        }

        // If controls are visible and the user lets go, start the hide timer.
        if (this.state.showControls) {
            this.scheduleControlsHide(this.CONTROLS_HIDE_MS);
        }
    };

    // Any activity while controls are visible keeps them alive
    handleUserActivity = () => {
        if (!this.state.showControls) return;
        this.scheduleControlsHide(this.CONTROLS_HIDE_MS);
    };

    handleTap = () => {
        if (!this.state.isMobile) return;

        // If this tap is the click that follows a long-press, ignore it.
        if (this._suppressNextClick) {
            this._suppressNextClick = false;
            return;
        }

        // Tap anywhere on mobile: unmute + ensure playing
        this.setState({
            muted: false,
            playing: true,
        });
    };

    buildStreamUrl = () => {
        const base = (this.state.streamBase || "").replace(/\/$/, "");
        const id = this.props.video?.id || this.props.id;
        return id ? `${base}/${id}` : "";
    };

    buildLightThumbUrl = () => {
        const thumbB64 = this.props.video?.cover?.thumb;
        if (!thumbB64) return true;

        const blob = b64toBlob(thumbB64);
        return URL.createObjectURL(blob);
    };

    componentWillUnmount() {
        this.clearControlsHideTimer();
        if (this._longPressTimer) {
            clearTimeout(this._longPressTimer);
            this._longPressTimer = null;
        }
    }

    render() {
        const { isMobile, muted, playing, showControls } = this.state;
        const streamUrl = this.buildStreamUrl();
        if (!streamUrl) return null;

        // Desktop: show preview thumbnail w/ play icon
        // Mobile: no light gate (so autoplay can work)
        const light = !isMobile ? this.buildLightThumbUrl() : false;

        return (
            <>
                <style>
                    {`
            .player-wrapper {
              padding: 0;
              margin: 0;
            }

            .player-wrapper video {
              width: 100% !important;
              height: 100% !important;
              object-fit: contain;
            }

            @media (max-width: 900px) {
              .player-wrapper video {
                width: 100% !important;
                height: 100% !important;
                object-fit: cover;
              }
            }

            .player-wrapper iframe {
              width: 100% !important;
              height: 100% !important;
            }

            .player-wrapper .react-player {
              margin-left: auto;
              margin-right: auto;
              display: flex;
              align-items: center;
              justify-content: center;
            }
          `}
                </style>

                <Box
                    className="player-wrapper"
                    sx={{
                        bgcolor: 'background.default',

                        // Mobile: fullscreen overlay
                        position: { xs: "fixed", md: "relative" },
                        inset: { xs: 0, md: "auto" },
                        zIndex: { xs: 1300, md: "auto" },

                        width: "100%",
                        height: { xs: "100dvh", md: "100vh" },

                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",

                        overflow: "hidden",

                        cursor: { xs: "pointer", md: "default" },
                    }}
                    // ✅ long press reveals controls
                    onPointerDown={this.handlePointerDown}
                    onPointerUp={this.handlePointerUpOrCancel}
                    onPointerCancel={this.handlePointerUpOrCancel}
                    // ✅ keep controls alive while interacting (scrolling finger/mouse)
                    onPointerMove={this.handleUserActivity}
                    onTouchStart={this.handleUserActivity}
                    onMouseMove={this.handleUserActivity}
                    // ✅ tap-to-unmute still works (unless long-press just fired)
                    onClick={isMobile ? this.handleTap : undefined}
                >
                    <Box
                        sx={{
                            height: "100%",
                            width: { xs: "100%", md: "min(100vw, calc(100vh * 9 / 16))" },
                            mx: { md: "auto" },
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <ReactPlayer
                            className="react-player"
                            url={streamUrl}
                            width="100%"
                            height="100%"

                            // ✅ controls hidden by default; shown only after long press
                            controls={showControls}

                            playing={playing}
                            muted={isMobile ? muted : false}
                            playsinline
                            light={light}
                            config={{
                                file: {
                                    forceVideo: true,
                                    attributes: {
                                        preload: "metadata",
                                        playsInline: true,
                                        playsinline: true,
                                        webkitPlaysInline: true,
                                    },
                                },
                            }}
                            playIcon={<PlayCircleIcon sx={{ fontSize: 72, color: 'text.secondary' }} />}
                            // extra: if they hit play on desktop preview, keep controls visible briefly
                            onPlay={() => this.showControlsTemporarily()}
                        />
                    </Box>

                    {/* Mobile prompt */}
                    {isMobile && muted && (
                        <Box
                            sx={{
                                position: "absolute",
                                left: 0,
                                right: 0,
                                bottom: 56,
                                display: "flex",
                                justifyContent: "center",
                                pointerEvents: "none",
                            }}
                        >
                            <Box
                                sx={{
                                    px: 2,
                                    py: 1,
                                    borderRadius: 999,
                                    bgcolor: (theme) => theme.custom.scrim,
                                }}
                            >
                                <Typography sx={{ color: 'text.primary', fontSize: 14 }}>
                                    Tap for sound
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Box>
            </>
        );
    }
}



//import React, { PureComponent } from "react";
//import { b64toBlob } from "../../DataServices/Utility";
//import { Box, Typography } from "@mui/material";
//import PlayCircleIcon from "@mui/icons-material/PlayCircle";
//import ReactPlayer from "react-player";

//export class VideoPlayer extends PureComponent {
//    constructor(props) {
//        super(props);

//        const isMobile =
//            typeof navigator !== "undefined" &&
//            /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

//        this.state = {
//            streamBase: "https://mylivesphere.com/api/videostream",
//            isMobile,
//            muted: isMobile,   // iOS autoplay requires muted
//            playing: isMobile, // autoplay on mobile
//        };
//    }

//    handleTap = () => {
//        if (!this.state.isMobile) return;

//        // Tap anywhere on mobile: unmute + ensure playing
//        this.setState({
//            muted: false,
//            playing: true,
//        });
//    };

//    buildStreamUrl = () => {
//        const base = (this.state.streamBase || "").replace(/\/$/, "");

//        // Two supported inputs:
//        // 1) video prop (preferred)
//        // 2) id prop (standalone route)
//        const id = this.props.video?.id || this.props.id;

//        return id ? `${base}/${id}` : "";
//    };

//    buildLightThumbUrl = () => {
//        // Only used for desktop "light" thumbnail mode.
//        // If video.cover.thumb exists, use it. Otherwise ReactPlayer uses a default preview behavior for light={true}.
//        const thumbB64 = this.props.video?.cover?.thumb;
//        if (!thumbB64) return true;

//        const blob = b64toBlob(thumbB64);
//        return URL.createObjectURL(blob);
//    };

//    componentWillUnmount() {
//        // If you generate object URLs often, you can revoke them.
//        // In this simple version we don't track the URL to revoke, but you can if needed.
//    }

//    render() {
//        const { isMobile, muted, playing } = this.state;
//        const isActive = !!this.props.isActive;
//        const streamUrl = this.buildStreamUrl();
//        if (!streamUrl) return null;

//        // Desktop: show preview thumbnail w/ play icon
//        // Mobile: no light gate (so autoplay can work)
//        const light = !isMobile ? this.buildLightThumbUrl() : false;

//        return (
//            <>
//                {/* One CSS block for both modes */}
//                <style>
//                    {`
//            .player-wrapper {
//              padding: 0;
//              margin: 0;
//            }

//            /* DESKTOP default: full-height, centered, preserve aspect ratio */
//            .player-wrapper video {
//              height: 100% !important;
//              width: auto !important;
//              object-fit: contain;
//            }
//.player-wrapper video {
//  width: 100% !important;
//  height: 100% !important;
//  object-fit: contain;
//}

//            /* MOBILE: full-bleed */
//            @media (max-width: 900px) {
//              .player-wrapper video {
//                width: 100% !important;
//                height: 100% !important;
//                object-fit: cover;
//              }
//            }

//            /* If using iframe providers (YouTube/Vimeo), still fill wrapper */
//            .player-wrapper iframe {
//              width: 100% !important;
//              height: 100% !important;
//            }
//.player-wrapper .react-player {
//  margin-left: auto;
//  margin-right: auto;
//  display: flex;
//  align-items: center;
//  justify-content: center;
//}

//          `}
//                </style>

//                <Box
//                    className="player-wrapper"
//                    sx={{
//                        bgcolor: 'background.default',

//                        // Mobile: fullscreen overlay
//                        position: { xs: "fixed", md: "relative" },
//                        inset: { xs: 0, md: "auto" },
//                        zIndex: { xs: 1300, md: "auto" },

//                        // Both: fill viewport height
//                        width: "100%",
//                        height: { xs: "100dvh", md: "100vh" },

//                        display: "flex",
//                        alignItems: "center",
//                        justifyContent: "center",

//                        overflow: "hidden",

//                        // Tap-to-unmute on mobile
//                        cursor: { xs: "pointer", md: "default" },
//                    }}
//                    onClick={isMobile ? this.handleTap : undefined}
//                >
//                    <Box
//                        sx={{
//                            height: "100%",
//                            // Desktop: width derived from viewport height (so it can't sprawl)
//                            width: { xs: "100%", md: "min(100vw, calc(100vh * 9 / 16))" },
//                            mx: { md: "auto" },
//                            display: "flex",
//                            alignItems: "center",
//                            justifyContent: "center",
//                        }}
//                    >
//                        <ReactPlayer
//                            className="react-player"
//                            url={streamUrl}
//                            width="100%"
//                            height="100%"
//                            controls
//                            playing={playing}
//                            muted={isMobile ? muted : false}
//                            playsinline
//                            light={light}
//                            config={{
//                                file: {
//                                    forceVideo: true,
//                                    attributes: {
//                                        preload: "metadata",
//                                        playsInline: true,
//                                        playsinline: true,
//                                        webkitPlaysInline: true,
//                                    },
//                                },
//                            }}
//                            playIcon={<PlayCircleIcon sx={{ fontSize: 72, color: 'text.secondary' }} />}
//                        />
//                    </Box>



//                    {/* Mobile prompt */}
//                    {isMobile && muted && (
//                        <Box
//                            sx={{
//                                position: "absolute",
//                                left: 0,
//                                right: 0,
//                                bottom: 56,
//                                display: "flex",
//                                justifyContent: "center",
//                                pointerEvents: "none",
//                            }}
//                        >
//                            <Box
//                                sx={{
//                                    px: 2,
//                                    py: 1,
//                                    borderRadius: 999,
//                                    bgcolor: (theme) => theme.custom.scrim,
//                                }}
//                            >
//                                <Typography sx={{ color: 'text.primary', fontSize: 14 }}>
//                                    Tap for sound
//                                </Typography>
//                            </Box>
//                        </Box>
//                    )}
//                </Box>
//            </>
//        );
//    }
//}

